/**
 * report-details-modal.component.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Component } from "@angular/core";
import { DialogRef, ModalComponent } from "angular2-modal";
import { BSModalContext } from "angular2-modal/plugins/bootstrap";
import { ReportStep } from "../algorithms/structure/report-step";

import { Observable } from "rxjs/Rx";
import { Graph, GraphEdge, GraphNode, StandardGraph } from "../graphs/graph";

/**
 * A modal context also containing an attached {@link ReportStep}.
 */
export class ReportContext extends BSModalContext
{
  public selected : ReportStep;
  public explanation : Observable<StandardGraph>;
  public important? : boolean;
}

/**
 * The {@link ReportDetailsModalComponent} controls the "report details" dialog
 */
@Component({
  template: `
  <div class="modal-header">
    <button
      type="button"
      class="close"
      (click)="dialog.dismiss()"
      aria-label="Close">
      <span aria-hidden="true">&times;</span>
    </button>
    <h4 class="modal-title">
      {{selected.displayName}}
    </h4>
  </div>

  <div *ngIf="important" class="modal-body">
    <em class="text-warning">This report was marked as important:</em>
  </div>

  <table>
    <tr>
      <td><badge *ngIf="selected" [report]="selected"></badge></td>
      <td [innerHtml]="selected.presentation"></td>
    </tr>
  </table>

  <table *ngIf="explanation" class="table fit-to-modal">
    <caption>
      Below are the intake steps leading to this determination
    </caption>
    <thead>
      <th>ID</th>
      <th>Name</th>
      <th>Value</th>
    </thead>
    <tbody>
      <tr
        *ngFor="let node of explanation?.nodes"
        [class.highlight]="highlight == node.id"
        (mouseenter)="highlight = node.id"
        (mouseleave)="highlight = null">
        <td>{{node.id}}</td>
        <td>{{node.step.displayName || "unknown"}}</td>
        <td>
          <typed-display
            *ngIf="node.category === 'IntakeStep'"
            [input]="node.step"></typed-display>
          <em *ngIf="node.category === 'ComputationStep'">
            (server computation)
		  </em>
			<em *ngIf="node.category === 'InterpolationStep'">
				(local calculation)
			</em>
          <em *ngIf="node.category === 'ReportStep'">
            (this report)
          </em>
        </td>
      </tr>
      <tr *ngIf="explanation?.nodes?.length === 0">
        <td colspan="3" class="text-center text-danger">
          No predecessors found.
        </td>
      </tr>
      <tr *ngIf="!explanation">
        <td colspan="3" class="text-center">
          <spinner></spinner>
        </td>
      </tr>
      <!--<tr
        [class.highlight]="highlight == selected.id"
        (mouseenter)="highlight = selected.id"
        (mouseleave)="highlight = null">
        <td>{{selected?.id}}</td>
        <td>{{selected?.name}}</td>
        <td>[this report]</td>
      </tr>-->
    </tbody>
  </table>

  <force-directed-graph
    *ngIf="explanation"
    [graph]="explanation"
    [highlight]="highlight"
    (selected)="highlight = $event">
  </force-directed-graph>

  <div class="modal-footer">
    <button
      type="button"
      class="btn btn-primary"
      (click)="dialog.close()">
      OK
    </button>
  </div>
  `,
  styles: [`
    table {
      width: 100%;
    }
    badge {
      margin: 15px;
    }
    /deep/ table td div p:last-child {
      margin-bottom: 0;
    }
    table .special-message td {
      text-align: center;
    }
    table caption {
      text-align: center;
      font-style: italic;
    }
    .fit-to-modal td:first-child, .fit-to-modal th:first-child {
      padding-left: 30px;
    }
    .fit-to-modal td:last-child, .fit-to-modal th:last-child {
      padding-right: 30px;
    }
    .table.fit-to-modal {
      margin-bottom: 0;
    }
    .table tbody {border-bottom: 1px solid #ddd;}
    .table tr.highlight {background-color: lightyellow;}
    `]
})
export class ReportDetailsModalComponent
  implements ModalComponent<ReportContext>
{
  /**
   * The selected report step for examination.
   * @type {ReportStep}
   */
  public selected : ReportStep;

  /**
   * The "explanation graph" displaying steps that trigger the report.
   */
  public explanation : StandardGraph;

  public important : boolean = false;

  /**
   * The ID of the currently-emphasized {@link AlgorithmStep}, if any. This is
   * used to correlate graph nodes with table rows and vice versa, and can
   * change due to hover interactions in either component.
   */
  public highlight : number | null = null;

  /**
   * Construct the {@link ReportDetailsModalComponent}
   * @param  {DialogRef<ReportContext>} dialog
   * @return {ReportDetailsModalComponent}
   */
  constructor (public dialog : DialogRef<ReportContext>)
  {
    this.selected = dialog.context.selected;
    dialog.context.explanation.subscribe((g : StandardGraph) => {
      this.explanation = g;
      g.nodes.sort(Graph.topologicalCompatator(g.edges));
    });

    if (dialog.context.important === true)
    {
      this.important = true;
    }
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/intake-form/report-details-modal.component.ts